import { apiUrl, apiUrlV2 } from "../constants/apiUrl"
import createSearchParams from "../utilities/createSearchParams"
import rollbarLogger from "../utilities/rollbarLogger"

const initialState = {
  notes: [],
  isFetchingNotes: false,
  isCreatingNote: false,
  isCreatingDealerNote: false,
  notesPaginationData: {},
  notesPage: 1,
  notesFilterParams: '',
  selectedNotesScopeLabel: null,
  notePurposeLabel: '',
  noteTypeLabel: '',
  unitNotes: [],
  isFetchingUnitNotes: false,
  isCreatingUnitNote: false,
  unitNotesPage: 1,
  unitNotesPaginationData: {},
}

const createNotesSlice = (set, get) => ({
  ...initialState,
  setNotesFilterParams: (params) => {
    set((state) => ({notesFilterParams: params}))
  },
  setSelectedNotesScopeLabel: (params) => {
    set((state) => ({selectedNotesScopeLabel: params}))
  },
  setNotePurposeLabel: (label) => set({ notePurposeLabel: label }),
  setNoteTypeLabel: (label) => set({ noteTypeLabel: label }),
  fetchNotes: async (dealerId, params) => {
    set(() => ({ isFetchingNotes: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/dealers/${dealerId}/combined_notes?page=${get().notesPage}&${createSearchParams(params)}`,
        { headers: get().requestHeaders }
      )
      const data = await response.json()

      set(() => ({
        notes: data.data,
        notesPaginationData: {
          dataTotal: response.headers.get('x-total'),
          lastPage: response.headers.get('x-last-page'),
          totalPages: response.headers.get('x-total-pages'),
          currentPage: Number(response.headers.get('x-page')) - 1
        }
      }))
    } catch (err) {
      get().logError('Unable to retrieve notes at this time.', err)
    } finally {
      set(() => ({ isFetchingNotes: false }))
    }
  },
  createDealerNote: async (dealerId, body) => {
    set(() => ({ isCreatingDealerNote: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/dealers/${dealerId}/combined_notes`,
        {
          headers: get().requestHeaders,
          method: 'POST',
          body: JSON.stringify({note: body})
        }
      )
      const data = await response.json()

      if (response.status === 200) {
        get().setSuccessMessage('Note or touch successfully created')
        get().fetchNotes(dealerId, '')
      } else {
        get().setErrMessage(data.errors[0])
      }
    } catch (err) {
      get().logError('There was an error creating this dealer note.', err)
    } finally {
      set(() => ({ isCreatingDealerNote: false }))
    }
  },
  setNotesPage: (page) => {
    set((state) => ({ notesPage: page }))
  },
  createNote: async (type, id, body) => {
    set(() => ({ isCreatingNote: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/${type}/${id}/notes`,
        {
          headers: get().requestHeaders,
          method: 'POST',
          body: JSON.stringify(body)
        }
      )
      const data = await response.json()

      if (response.status === 200) {
        get().setSuccessMessage('Note successfully created')
        get().fetchNotes(type, id)
      } else {
        get().setErrMessage(data.errors[0])
      }
    } catch (err) {
      get().logError('There was an error creating this note.', err)
    } finally {
      set(() => ({ isCreatingNote: false }))
    }
  },
  setUnitNotesPage: (page) => {
    set(() => ({ unitNotesPage: page }))
  },
  fetchUnitNotes: async (unitId) => {
    set(() => ({ isFetchingUnitNotes: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/units/${unitId}/notes?page=${get().unitNotesPage}&per_page=10`,
        { headers: get().requestHeaders }
      )
      const data = await response.json()
      set(() => ({
        unitNotes: data.data,
        unitNotesPaginationData: {
          dataTotal: response.headers.get('x-total'),
          lastPage: response.headers.get('x-last-page'),
          totalPages: response.headers.get('x-total-pages'),
          currentPage: Number(response.headers.get('x-page')) - 1
        }
      }))
    } catch (err) {
      get().logError('Unable to retrieve unit notes at this time.', err)
    } finally {
      set(() => ({ isFetchingUnitNotes: false }))
    }
  },
  createUnitNote: async (unitId, body) => {
    set(() => ({ isCreatingUnitNote: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/units/${unitId}/notes`,
        {
          headers: get().requestHeaders,
          method: 'POST',
          body: JSON.stringify({ note: body })
        }
      )
      const data = await response.json()

      if (response.status === 200) {
        get().setSuccessMessage('Note successfully created')
        get().fetchUnitNotes(unitId)
      } else {
        get().setErrMessage(data.errors[0])
      }
    } catch (err) {
      get().logError('There was an error creating this unit note.', err)
    } finally {
      set(() => ({ isCreatingUnitNote: false }))
    }
  },
  resetNotesSlice: () => set(initialState)
})

export default createNotesSlice